import type { OrigemAbaAuditoria } from '@/shared/types/contextoAuditoria';

import { paraHeaderGeo } from './geoPosicao';
import { declararColetaDePosicao, posicaoDaJanelaCorrente } from './posicaoEmCache';

/**
 * Contexto que a aba Auditoria do processo grava junto com cada ato: de onde no
 * sistema a pessoa estava (`X-Origem-Aba`) e onde ela estava (`X-Client-Geo`).
 *
 * Estado de módulo em vez de contexto de React porque quem consome é o
 * `requestBuilder`, que não vive na árvore de componentes. A leitura da posição em si mora em
 * `posicaoEmCache`, compartilhada com o portal do notificado — aqui fica só a política desta
 * superfície.
 *
 * **Opt-in por tela.** Enquanto nenhuma tela declarar a aba, nada é enviado —
 * geolocalização não é dado que se manda por padrão em toda request só porque o
 * navegador concedeu permissão uma vez.
 */

let abaCorrente: OrigemAbaAuditoria | null = null;

/**
 * Declara a aba atual e obtém a posição, se a anterior já venceu.
 *
 * A posição é resolvida aqui, não no momento da request: pedi-la dentro do fluxo
 * de envio somaria até 10s de latência a uma ação do usuário.
 *
 * Permissão negada ⇒ segue sem coordenada; a aba mostra "—".
 */
export async function declararOrigemAba(aba: OrigemAbaAuditoria): Promise<void> {
  abaCorrente = aba;
  await declararColetaDePosicao();
}

/** Encerra a declaração — chamado no cleanup da tela que a abriu. */
export function limparOrigemAba(): void {
  abaCorrente = null;
}

/**
 * Headers do contexto, só para requests de MUTAÇÃO.
 *
 * `GET` fica de fora de propósito: a trilha não registra visualização, então
 * mandar a coordenada numa leitura seria enviar dado pessoal que nenhum evento
 * vai consumir. (No portal do notificado a regra é a oposta, e por isso ele tem
 * construtor próprio: lá o acesso É o evento.)
 */
export async function headersDeContextoAuditoria(metodo: string): Promise<Record<string, string>> {
  if (!abaCorrente || metodo === 'GET') return {};

  const geo = paraHeaderGeo(await posicaoDaJanelaCorrente());
  return {
    'X-Origem-Aba': abaCorrente,
    ...(geo ? { 'X-Client-Geo': geo } : {}),
  };
}
